import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import Header from "../../components/ui/Header";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import Icon from "../../components/AppIcon";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { getAuthData } from "utils/auth";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("account");
  const [isLoading, setIsLoading] = useState(false);

  const { user: currentUser } = getAuthData();

  // Form handling for account settings
  const {
    register: registerAccount,
    handleSubmit: handleSubmitAccount,
    formState: { errors: errorsAccount },
  } = useForm({
    defaultValues: {
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      phone: "+1 (555) 123-4567",
      timezone: "America/New_York",
    },
  });

  // Form handling for notifications
  const {
    register: registerNotifications,
    handleSubmit: handleSubmitNotifications,
    watch: watchNotifications,
  } = useForm({
    defaultValues: {
      emailExpenses: true,
      emailSettlements: true,
      emailReminders: false,
      pushExpenses: true,
      pushSettlements: true,
      pushReminders: true,
      weeklyDigest: false,
      monthlyReport: true,
    },
  });

  // Form handling for display settings
  const { register: registerDisplay, handleSubmit: handleSubmitDisplay } =
    useForm({
      defaultValues: {
        currency: "INR",
        dateFormat: "DD/MM/YYYY", // Changed to Indian date format
        timeFormat: "12",
        language: "en",
        theme: "system",
      },
    });

  const tabs = [
    { id: "account", label: "Account", icon: "User" },
    { id: "notifications", label: "Notifications", icon: "Bell" },
    { id: "display", label: "Display", icon: "Monitor" },
    { id: "privacy", label: "Privacy", icon: "Shield" },
    { id: "advanced", label: "Advanced", icon: "Settings2" },
  ];

  const currencies = [
    { code: "INR", name: "Indian Rupee (₹)", symbol: "₹" },
    { code: "USD", name: "US Dollar ($)", symbol: "$" },
    { code: "EUR", name: "Euro (€)", symbol: "€" },
    { code: "GBP", name: "British Pound (£)", symbol: "£" },
    { code: "JPY", name: "Japanese Yen (¥)", symbol: "¥" },
    { code: "CAD", name: "Canadian Dollar ($)", symbol: "C$" },
    { code: "AUD", name: "Australian Dollar ($)", symbol: "A$" },
  ];

  const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "pt", name: "Portuguese" },
  ];

  const onSubmitAccount = async (data) => {
    setIsLoading(true);
    console.log("Account settings:", data);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    alert("Account settings updated successfully!");
  };

  const onSubmitNotifications = async (data) => {
    setIsLoading(true);
    console.log("Notification settings:", data);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsLoading(false);
    alert("Notification preferences updated!");
  };

  const onSubmitDisplay = async (data) => {
    setIsLoading(true);
    console.log("Display settings:", data);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsLoading(false);
    alert("Display settings updated!");
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data, events, and expense history."
    );
    if (confirmed) {
      console.log("Account deletion requested");
      alert(
        "Account deletion request submitted. You will receive a confirmation email within 24 hours."
      );
    }
  };

  const handleExportData = () => {
    console.log("Exporting user data");
    alert(
      "Your data export has been initiated. You'll receive an email with a download link within the next few minutes."
    );
  };

  const renderAccountSettings = () => (
    <form onSubmit={handleSubmitAccount(onSubmitAccount)} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Full Name *
            </label>
            <Input
              id="name"
              type="text"
              {...registerAccount("name", { required: "Name is required" })}
              error={errorsAccount?.name?.message}
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Email Address *
            </label>
            <Input
              id="email"
              type="email"
              {...registerAccount("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              error={errorsAccount?.email?.message}
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Phone Number
            </label>
            <Input id="phone" type="tel" {...registerAccount("phone")} />
          </div>

          <div>
            <label
              htmlFor="timezone"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Timezone
            </label>
            <select
              id="timezone"
              {...registerAccount("timezone")}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">GMT</option>
              <option value="Europe/Paris">Central European Time</option>
              <option value="Asia/Tokyo">Japan Standard Time</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Change Password
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Current Password
            </label>
            <Input
              id="currentPassword"
              type="password"
              {...registerAccount("currentPassword")}
            />
          </div>
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-foreground mb-1"
            >
              New Password
            </label>
            <Input
              id="newPassword"
              type="password"
              {...registerAccount("newPassword")}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <Button
          type="submit"
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );

  const renderNotificationSettings = () => (
    <form
      onSubmit={handleSubmitNotifications(onSubmitNotifications)}
      className="space-y-6"
    >
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Email Notifications
        </h3>
        <div className="space-y-4">
          {[
            {
              key: "emailExpenses",
              label: "New expenses added",
              description:
                "Get notified when someone adds an expense to your events",
            },
            {
              key: "emailSettlements",
              label: "Settlement requests",
              description:
                "Receive notifications when someone wants to settle up",
            },
            {
              key: "emailReminders",
              label: "Payment reminders",
              description: "Reminders about outstanding balances",
            },
            {
              key: "weeklyDigest",
              label: "Weekly digest",
              description: "Summary of your week's expense activity",
            },
            {
              key: "monthlyReport",
              label: "Monthly report",
              description: "Detailed monthly expense and balance report",
            },
          ]?.map((item) => (
            <div key={item?.key} className="flex items-start space-x-3">
              <input
                type="checkbox"
                id={item?.key}
                {...registerNotifications(item?.key)}
                className="mt-1 h-4 w-4 text-primary focus:ring-ring border-border rounded"
              />
              <div className="flex-1">
                <label
                  htmlFor={item?.key}
                  className="text-sm font-medium text-foreground cursor-pointer"
                >
                  {item?.label}
                </label>
                <p className="text-xs text-muted-foreground">
                  {item?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Push Notifications
        </h3>
        <div className="space-y-4">
          {[
            {
              key: "pushExpenses",
              label: "New expenses",
              description: "Instant notifications for new expenses",
            },
            {
              key: "pushSettlements",
              label: "Settlement updates",
              description: "Real-time settlement notifications",
            },
            {
              key: "pushReminders",
              label: "Balance reminders",
              description: "Push reminders for outstanding balances",
            },
          ]?.map((item) => (
            <div key={item?.key} className="flex items-start space-x-3">
              <input
                type="checkbox"
                id={item?.key}
                {...registerNotifications(item?.key)}
                className="mt-1 h-4 w-4 text-primary focus:ring-ring border-border rounded"
              />
              <div className="flex-1">
                <label
                  htmlFor={item?.key}
                  className="text-sm font-medium text-foreground cursor-pointer"
                >
                  {item?.label}
                </label>
                <p className="text-xs text-muted-foreground">
                  {item?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <Button
          type="submit"
          loading={isLoading}
          iconName="Bell"
          iconPosition="left"
        >
          Update Notifications
        </Button>
      </div>
    </form>
  );

  const renderDisplaySettings = () => (
    <form onSubmit={handleSubmitDisplay(onSubmitDisplay)} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Currency & Format
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="currency"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Default Currency
            </label>
            <select
              id="currency"
              {...registerDisplay("currency")}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              {currencies?.map((currency) => (
                <option key={currency?.code} value={currency?.code}>
                  {currency?.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              This will be your default currency for new events
            </p>
          </div>

          <div>
            <label
              htmlFor="dateFormat"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Date Format
            </label>
            <select
              id="dateFormat"
              {...registerDisplay("dateFormat")}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</option>
              <option value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2023)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="timeFormat"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Time Format
            </label>
            <select
              id="timeFormat"
              {...registerDisplay("timeFormat")}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              <option value="12">12-hour (3:30 PM)</option>
              <option value="24">24-hour (15:30)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="language"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Language
            </label>
            <select
              id="language"
              {...registerDisplay("language")}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            >
              {languages?.map((language) => (
                <option key={language?.code} value={language?.code}>
                  {language?.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Appearance
        </h3>
        <div>
          <label
            htmlFor="theme"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Theme
          </label>
          <select
            id="theme"
            {...registerDisplay("theme")}
            className="w-full max-w-xs px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          >
            <option value="system">System preference</option>
            <option value="light">Light mode</option>
            <option value="dark">Dark mode</option>
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose how ExpenseLoop looks and feels
          </p>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <Button
          type="submit"
          loading={isLoading}
          iconName="Palette"
          iconPosition="left"
        >
          Apply Changes
        </Button>
      </div>
    </form>
  );

  const renderPrivacySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Data & Privacy
        </h3>
        <div className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground mb-1">
                  Profile Visibility
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Control who can see your profile and add you to events
                </p>
                <select className="px-3 py-1.5 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="everyone">Everyone</option>
                  <option value="friends">Friends only</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <Icon name="Eye" size={20} className="text-muted-foreground" />
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground mb-1">
                  Data Analytics
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Allow ExpenseLoop to use your data to improve the service
                </p>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-ring border-border rounded"
                    defaultChecked={false}
                  />
                  <span className="ml-2 text-sm text-foreground">
                    Enable analytics
                  </span>
                </label>
              </div>
              <Icon
                name="BarChart3"
                size={20}
                className="text-muted-foreground"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Data Management
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Export Your Data
              </h4>
              <p className="text-xs text-muted-foreground">
                Download a copy of all your ExpenseLoop data
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportData}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Clear Cache
              </h4>
              <p className="text-xs text-muted-foreground">
                Clear locally stored data to free up space
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Integrations
        </h3>
        <div className="space-y-4">
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Icon name="CreditCard" size={20} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">
                    PayPal Integration
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Connect PayPal for easy settlements
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Connect
              </Button>
            </div>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Icon
                    name="Smartphone"
                    size={20}
                    className="text-green-600"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">
                    Venmo Integration
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Link Venmo for quick payments
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Connect
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Danger Zone
        </h3>
        <div className="space-y-3">
          <div className="p-4 border border-red-300 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  Delete Account
                </h4>
                <p className="text-xs text-red-600">
                  Permanently delete your account and all associated data. This
                  action cannot be undone.
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteAccount}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "account":
        return renderAccountSettings();
      case "notifications":
        return renderNotificationSettings();
      case "display":
        return renderDisplaySettings();
      case "privacy":
        return renderPrivacySettings();
      case "advanced":
        return renderAdvancedSettings();
      default:
        return renderAccountSettings();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Settings - ExpenseLoop</title>
        <meta
          name="description"
          content="Manage your ExpenseLoop account settings, notifications, display preferences, and privacy controls."
        />
      </Helmet>

      {/* <Header user={currentUser} isAuthenticated={true} /> */}

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />

          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Settings" size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground">
                  Manage your account preferences and customize your ExpenseLoop
                  experience
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-micro ${
                        activeTab === tab?.id
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon name={tab?.icon} size={20} />
                      <span className="font-medium">{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
